export type PendingInputKind =
  | 'clarification'
  | 'artifact_review'
  | 'approval_gate'
  | 'resumable_run';

export type PendingInputPriority = 'high' | 'normal';

export type PendingInputAction =
  | 'resume'
  | 'approve'
  | 'request_changes'
  | 'discuss'
  | 'open_artifact'
  | 'view_details';

export type PendingInputItem = {
  id: string;
  kind: PendingInputKind;
  title: string;
  description: string;
  actionNeeded: string;
  priority: PendingInputPriority;
  actions: PendingInputAction[];
  requestId?: string;
  runId?: string;
  threadId?: string;
  messageId?: string;
  artifactId?: string;
  artifactTitle?: string;
  status?: string;
};

type ClarifyRequestLike = {
  id?: string;
  type?: string;
  questions?: string[];
  agentLabel?: string;
  originRunId?: string;
  createdAt?: number;
};

type MessageLike = {
  id?: string;
  sender?: string;
  content?: string;
  text?: string;
  messageKind?: string;
  meta?: unknown;
  timestamp?: unknown;
};

type ArtifactLike = {
  id?: string;
  title?: string;
  runId?: string;
  artifactKey?: string;
  lineageId?: string;
  approvalStatus?: string;
};

type AggregatePendingInputItemsInput = {
  clarifyPending?: ClarifyRequestLike | null;
  clarifyQueue?: ClarifyRequestLike[] | null;
  messages?: MessageLike[] | null;
  artifacts?: ArtifactLike[] | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeStatus(value: unknown): string {
  return normalizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

function formatType(value: unknown): string {
  const raw = normalizeString(value);
  if (!raw) return 'general';
  return raw.replace(/_/g, ' ');
}

function truncate(value: string, max = 120): string {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

function artifactTitle(artifact: ArtifactLike | undefined | null): string | undefined {
  const title = normalizeString(artifact?.title);
  return title || undefined;
}

function parseArtifactFeedbackMeta(message: MessageLike): {
  artifactId: string;
  lineageId?: string;
  runId?: string;
  promptMessageId?: string;
  action?: string;
} | null {
  const meta = asRecord(message.meta);
  const raw = asRecord(meta?.artifactFeedback);
  if (!raw) return null;
  const artifactId = normalizeString(raw.artifactId);
  if (!artifactId) return null;
  return {
    artifactId,
    ...(normalizeString(raw.lineageId) ? { lineageId: normalizeString(raw.lineageId) } : {}),
    ...(normalizeString(raw.runId) ? { runId: normalizeString(raw.runId) } : {}),
    ...(normalizeString(raw.promptMessageId) ? { promptMessageId: normalizeString(raw.promptMessageId) } : {}),
    ...(normalizeString(raw.action) ? { action: normalizeString(raw.action) } : {}),
  };
}

function findArtifact(artifacts: ArtifactLike[], artifactId?: string): ArtifactLike | undefined {
  if (!artifactId) return undefined;
  return artifacts.find((artifact) => artifact.id === artifactId);
}

function isFinalArtifactFeedbackAction(action: string | undefined): boolean {
  return action === 'approve' || action === 'request_changes';
}

function isFinalArtifactStatus(status: unknown): boolean {
  const normalized = normalizeStatus(status);
  return normalized === 'approved' || normalized === 'needs_revision' || normalized === 'rejected';
}

function runtimeStateFromMessage(message: MessageLike): Record<string, unknown> | null {
  const meta = asRecord(message.meta);
  const runtimeTool = asRecord(meta?.runtimeTool);
  return asRecord(runtimeTool?.state);
}

function hasApprovalGate(message: MessageLike): boolean {
  const meta = asRecord(message.meta);
  const pendingStatus = normalizeStatus(meta?.pendingStatus);
  const state = runtimeStateFromMessage(message);
  const stateStatus = normalizeStatus(state?.status);
  const stateStage = normalizeStatus(state?.stage);
  const summary = `${normalizeString(state?.summary)} ${normalizeString(state?.label)} ${normalizeString(message.content)} ${normalizeString(message.text)}`.toLowerCase();

  return (
    /approval|pending_review|human_review|needs_review|approve/.test(pendingStatus)
    || /approval|pending_review|human_review|needs_review|approve/.test(stateStatus)
    || /approval|pending_review|human_review|needs_review|approve/.test(stateStage)
    || (
      (stateStatus === 'pending' || stateStatus === 'running')
      && /approval|review gate|human input/.test(summary)
    )
  );
}

function isResumableStatus(value: unknown): boolean {
  const status = normalizeStatus(value);
  return (
    status === 'needs_clarification'
    || status === 'clarification_needed'
    || status === 'needs_input'
    || status === 'interrupted'
    || status === 'paused'
    || status === 'expired'
  );
}

function runIdFromMessage(message: MessageLike): string | undefined {
  const meta = asRecord(message.meta);
  const state = runtimeStateFromMessage(message);
  return (
    normalizeString(meta?.runId)
    || normalizeString(state?.runId)
    || normalizeString(state?.requestId)
    || undefined
  );
}

function threadIdFromMessage(message: MessageLike): string | undefined {
  const meta = asRecord(message.meta);
  const provenance = asRecord(meta?.provenance);
  const state = runtimeStateFromMessage(message);
  return (
    normalizeString(meta?.threadId)
    || normalizeString(provenance?.threadId)
    || normalizeString(state?.threadId)
    || normalizeString(state?.sourceConversationId)
    || undefined
  );
}

function pushUnique(items: PendingInputItem[], seen: Set<string>, item: PendingInputItem): void {
  if (seen.has(item.id)) return;
  seen.add(item.id);
  items.push(item);
}

export function aggregatePendingInputItems(input: AggregatePendingInputItemsInput): PendingInputItem[] {
  const messages = input.messages || [];
  const artifacts = input.artifacts || [];
  const items: PendingInputItem[] = [];
  const seen = new Set<string>();

  const resolvedArtifactPrompts = new Set<string>();
  for (const message of messages) {
    if (message.messageKind !== 'artifact_feedback_response') continue;
    const feedback = parseArtifactFeedbackMeta(message);
    if (!feedback || !isFinalArtifactFeedbackAction(feedback.action)) continue;
    resolvedArtifactPrompts.add(feedback.promptMessageId || feedback.artifactId);
  }

  const clarificationRequests = input.clarifyQueue?.length
    ? input.clarifyQueue
    : input.clarifyPending
      ? [input.clarifyPending]
      : [];

  for (const request of clarificationRequests) {
    const requestId = normalizeString(request.id);
    if (!requestId) continue;
    const questionCount = Array.isArray(request.questions) ? request.questions.length : 0;
    pushUnique(items, seen, {
      id: `clarification:${requestId}`,
      kind: 'clarification',
      title: `${normalizeString(request.agentLabel) || 'Agent'} needs details`,
      description: questionCount
        ? `${questionCount} ${questionCount === 1 ? 'question' : 'questions'} for ${formatType(request.type)} context.`
        : `Clarification requested for ${formatType(request.type)} context.`,
      actionNeeded: 'Answer or skip the clarification request.',
      priority: 'high',
      actions: ['resume', 'view_details'],
      requestId,
      ...(normalizeString(request.originRunId) ? { runId: normalizeString(request.originRunId) } : {}),
      status: 'needs_input',
    });
  }

  for (const message of messages) {
    const messageId = normalizeString(message.id);
    if (message.messageKind === 'artifact_feedback_prompt') {
      const feedback = parseArtifactFeedbackMeta(message);
      const artifact = findArtifact(artifacts, feedback?.artifactId);
      const promptKey = feedback?.promptMessageId || messageId || feedback?.artifactId || '';
      if (feedback && !resolvedArtifactPrompts.has(promptKey) && !isFinalArtifactStatus(artifact?.approvalStatus)) {
        const title = artifactTitle(artifact) || 'Draft artifact';
        pushUnique(items, seen, {
          id: `artifact-review:${promptKey}`,
          kind: 'artifact_review',
          title: 'Draft ready for review',
          description: truncate(title, 96),
          actionNeeded: 'Approve, request changes, discuss, or open the artifact.',
          priority: 'high',
          actions: ['approve', 'request_changes', 'discuss', 'open_artifact'],
          ...(messageId ? { messageId } : {}),
          requestId: promptKey,
          artifactId: feedback.artifactId,
          artifactTitle: title,
          ...(feedback.runId ? { runId: feedback.runId } : {}),
          status: 'pending_review',
        });
      }
    }

    if (hasApprovalGate(message)) {
      const requestId = messageId || runIdFromMessage(message) || `approval-${items.length}`;
      pushUnique(items, seen, {
        id: `approval-gate:${requestId}`,
        kind: 'approval_gate',
        title: 'Approval gate waiting',
        description: 'A graph step is paused for explicit human confirmation.',
        actionNeeded: 'Review the run details before continuing.',
        priority: 'high',
        actions: ['view_details', 'discuss'],
        ...(messageId ? { messageId } : {}),
        requestId,
        ...(runIdFromMessage(message) ? { runId: runIdFromMessage(message) } : {}),
        ...(threadIdFromMessage(message) ? { threadId: threadIdFromMessage(message) } : {}),
        status: normalizeStatus(asRecord(message.meta)?.pendingStatus || runtimeStateFromMessage(message)?.status) || 'pending_approval',
      });
    }

    const meta = asRecord(message.meta);
    const pendingStatus = meta?.pendingStatus;
    if (isResumableStatus(pendingStatus)) {
      const runId = runIdFromMessage(message);
      const requestId = messageId || runId || `resumable-${items.length}`;
      const alreadyHasClarificationForRun = !!runId && items.some((item) => (
        item.kind === 'clarification' && item.runId === runId
      ));
      if (!alreadyHasClarificationForRun) {
        pushUnique(items, seen, {
          id: `resumable-run:${requestId}`,
          kind: 'resumable_run',
          title: 'Run can be resumed',
          description: `Status: ${formatType(pendingStatus)}.`,
          actionNeeded: 'Resume from the latest checkpoint or discuss next steps.',
          priority: 'normal',
          actions: ['resume', 'discuss', 'view_details'],
          ...(messageId ? { messageId } : {}),
          requestId,
          ...(runId ? { runId } : {}),
          ...(threadIdFromMessage(message) ? { threadId: threadIdFromMessage(message) } : {}),
          status: normalizeStatus(pendingStatus),
        });
      }
    }
  }

  return items.sort((left, right) => {
    if (left.priority !== right.priority) return left.priority === 'high' ? -1 : 1;
    const order: Record<PendingInputKind, number> = {
      clarification: 0,
      artifact_review: 1,
      approval_gate: 2,
      resumable_run: 3,
    };
    return order[left.kind] - order[right.kind];
  });
}
